import { orderDataToBookings, bookingsDataToProcedures, orderDataToOrders, nullishFilter, byIdArrayToObj, objectMap, arrayToObj } from '@caresend/utils';
import update from 'immutability-helper';
import { isProd } from '../../database/firebaseConfig/init.js';
import { hasMutation, customCommit } from '../helpers.js';
import { setDraftWaypoints } from './draftWaypoints.js';
import { setOffices } from './office.js';
import { setTransactions } from './transactions.js';
import { setStaffUsers, setUsers } from './users.js';

/**
 * Takes an `OrderData` array and sets orders, bookings, procedures, waypoints and waypointActions in the
 * appropriate store modules.
 *
 * Required mutations:
 * - `'bookings/SET_BOOKINGS' - ByID<Booking>`
 * - `'orders/SET_ORDERS' - ByID<Order>`
 * - `'procedures/SET_PROCEDURES' - ByID<WithFriendlyStatus<Procedure>>`
 *
 * Optional mutations:
 * - `'users/SET_STAFF_USERS' - StaffUser[]`
 * - `'users/SET_USERS' - StaffUser[]`
 * - `'offices/SET_OFFICES' - ByID<Office>`
 * - `'waypoint/SET_WAYPOINTS' - ByID<EncryptedWaypoint>`
 * - `'waypoint/SET_WAYPOINT_ACTIONS' - ByID<WaypointAction>`
 * - `'transactions/SET_TRANSACTIONS' - ByID<Transaction>`
 * - `'waypoint/SET_DRAFT_WAYPOINTS' - ByID<UnencryptedDraftWaypoint>`
 *
 * Must pass `store` argument to enable dev logging and optional mutations.
 * In actions, you may pass `this`, if you use the following syntax:
 * ```
 *  async 'orderFlow/fetchBookingForViewing'(
 *    this: CustomRootStore,
 *    { commit, getters },
 *    bookingID,
 *  ) {
 * ```
 */
const setOrderDataInStore = (commit, orderData, 
/** Pass store to enable dev logging and optional mutations */
store) => {
    /** === Required === */
    /** Bookings */
    const { bookings, bookingsData } = orderDataToBookings(orderData);
    /** Procedures */
    const { procedures, proceduresData } = bookingsDataToProcedures(bookingsData);
    /** Orders */
    const { orders } = orderDataToOrders(orderData);
    /** Set Required */
    commit('orders/SET_ORDERS', orders);
    commit('bookings/SET_BOOKINGS', bookings);
    commit('procedures/SET_PROCEDURES', procedures);
    /** === Optional === */
    /** WaypointActions */
    let waypointActionsSet = 'N/A';
    const waypointActionDataByIdArray = Object.values(proceduresData)
        .map((procedure) => procedure.waypointActionsData)
        .filter(nullishFilter);
    const waypointActionsWithData = byIdArrayToObj(waypointActionDataByIdArray);
    const waypointActions = objectMap(waypointActionsWithData, (waypointAction) => update(waypointAction, { $unset: [
            'waypointData',
            'proceduresData',
        ] }));
    if (hasMutation(store, 'waypoint/SET_WAYPOINT_ACTIONS')) {
        waypointActionsSet = Object.keys(waypointActions).length;
        customCommit(commit, 'waypoint/SET_WAYPOINT_ACTIONS', waypointActions);
    }
    /** Waypoints */
    let waypointsSet = 'N/A';
    const waypointDataByIdArray = Object.values(waypointActionsWithData)
        .map((waypointAction) => waypointAction.waypointData)
        .filter(nullishFilter);
    const waypointsWithData = arrayToObj(waypointDataByIdArray, 'id');
    const waypoints = objectMap(waypointsWithData, (waypoint) => update(waypoint, { $unset: [
            'waypointActionsData',
            'itineraryData',
        ] }));
    if (hasMutation(store, 'waypoint/SET_WAYPOINTS')) {
        waypointsSet = Object.keys(waypoints).length;
        customCommit(commit, 'waypoint/SET_WAYPOINTS', waypoints);
    }
    /** StaffUsers (Nurses, Prescribers) */
    const { staffUsersSet, staffUsers } = setStaffUsers(commit, proceduresData, orderData, store);
    /** Users  (Patients) */
    const { usersSet, users } = setUsers(commit, bookingsData, store);
    /** Offices */
    const { officesSet, offices } = setOffices(commit, orderData, store);
    /** Transactions */
    const { transactionsSet, transactions } = setTransactions(commit, orderData, store);
    /** DraftWaypoints */
    const { draftWaypointsSet, draftWaypoints } = setDraftWaypoints(commit, orderData, store);
    /** Logs (dev mode only) */
    if (!isProd && !process.env.VUE_APP_SUPPRESS_LOGS) {
        console.info('setOrderDataInStore has set:');
        console.table({
            orders: orderData.length,
            bookings: Object.keys(bookings).length,
            draftWaypoints: draftWaypointsSet,
            procedures: Object.keys(procedures).length,
            offices: officesSet,
            staffUsers: staffUsersSet,
            users: usersSet,
            transactions: transactionsSet,
            waypoints: waypointsSet,
            waypointActions: waypointActionsSet,
        });
    }
    return {
        bookings,
        draftWaypoints,
        orders,
        offices,
        procedures,
        staffUsers,
        users,
        transactions,
        waypointActions,
        waypoints,
    };
};

export { setOrderDataInStore };
